import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { type RootState } from '@/platform/store/types';
import {
  type Product,
  type ProductStats,
  type ProductCategory,
  type ProductErrorCode,
} from '../models/product.model';
import type { WithSlice } from '@reduxjs/toolkit';

export interface ProductSliceError {
  code: ProductErrorCode;
  detail: string;
}

export type ProductListStatus = 'idle' | 'loading' | 'success' | 'error';
export type ProductCurrentStatus =
  | 'idle'
  | 'loading'
  | 'saving'
  | 'success'
  | 'error';

export interface ProductState {
  list: {
    data: Product[];
    total: number;
    status: ProductListStatus;
    error: ProductSliceError | null;
  };
  stats: {
    data: ProductStats;
    status: 'idle' | 'loading' | 'success' | 'error';
  };
  categories: {
    data: ProductCategory[];
    status: 'idle' | 'loading' | 'success' | 'error';
  };
  current: {
    id: string | null;
    data: Product | null;
    status: ProductCurrentStatus;
    error: ProductSliceError | null;
  };
  bulkSelection: string[];
}

const initialState: ProductState = {
  list: { data: [], total: 0, status: 'idle', error: null },
  stats: {
    data: { active: 0, draft: 0, low_stock: 0, out_of_stock: 0 },
    status: 'idle',
  },
  categories: { data: [], status: 'idle' },
  current: { id: null, data: null, status: 'idle', error: null },
  bulkSelection: [],
};

export const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    // List
    setListLoading: (state) => {
      state.list.status = 'loading';
      state.list.error = null;
    },
    setListData: (
      state,
      action: PayloadAction<{ products: Product[]; total: number }>,
    ) => {
      state.list.status = 'success';
      state.list.data = action.payload.products;
      state.list.total = action.payload.total;
      state.list.error = null;
    },
    setListError: (state, action: PayloadAction<ProductSliceError>) => {
      state.list.status = 'error';
      state.list.error = action.payload;
    },
    upsertListItem: (state, action: PayloadAction<Product>) => {
      const idx = state.list.data.findIndex(
        (p) => p._id === action.payload._id,
      );
      if (idx >= 0) {
        state.list.data[idx] = action.payload;
      } else {
        state.list.data.unshift(action.payload);
      }
    },
    removeListItem: (state, action: PayloadAction<string>) => {
      state.list.data = state.list.data.filter(
        (p) => p._id !== action.payload,
      );
    },

    // Stats
    setStatsLoading: (state) => {
      state.stats.status = 'loading';
    },
    setStatsData: (state, action: PayloadAction<ProductStats>) => {
      state.stats.status = 'success';
      state.stats.data = action.payload;
    },
    setStatsError: (state) => {
      state.stats.status = 'error';
    },

    // Categories
    setCategoriesLoading: (state) => {
      state.categories.status = 'loading';
    },
    setCategoriesData: (state, action: PayloadAction<ProductCategory[]>) => {
      state.categories.status = 'success';
      state.categories.data = action.payload;
    },
    setCategoriesError: (state) => {
      state.categories.status = 'error';
    },

    // Current
    setCurrentLoading: (state) => {
      state.current.status = 'loading';
      state.current.error = null;
    },
    setCurrentData: (state, action: PayloadAction<Product>) => {
      state.current.status = 'success';
      state.current.id = action.payload._id;
      state.current.data = action.payload;
      state.current.error = null;
    },
    setCurrentSaving: (state) => {
      state.current.status = 'saving';
    },
    setCurrentError: (state, action: PayloadAction<ProductSliceError>) => {
      state.current.status = 'error';
      state.current.error = action.payload;
    },
    clearCurrent: (state) => {
      state.current = initialState.current;
    },

    // Bulk selection
    setBulkSelection: (state, action: PayloadAction<string[]>) => {
      state.bulkSelection = action.payload;
    },
    toggleBulkItem: (state, action: PayloadAction<string>) => {
      const idx = state.bulkSelection.indexOf(action.payload);
      if (idx >= 0) {
        state.bulkSelection.splice(idx, 1);
      } else {
        state.bulkSelection.push(action.payload);
      }
    },
    clearBulkSelection: (state) => {
      state.bulkSelection = [];
    },
  },
});

export const {
  setListLoading,
  setListData,
  setListError,
  upsertListItem,
  removeListItem,
  setStatsLoading,
  setStatsData,
  setStatsError,
  setCategoriesLoading,
  setCategoriesData,
  setCategoriesError,
  setCurrentLoading,
  setCurrentData,
  setCurrentSaving,
  setCurrentError,
  clearCurrent,
  setBulkSelection,
  toggleBulkItem,
  clearBulkSelection,
} = productSlice.actions;

// Selectors
const selectSlice = (state: RootState): ProductState =>
  state.product ?? initialState;

export const selectProductList = (state: RootState) => selectSlice(state).list;
export const selectProductStats = (state: RootState) => selectSlice(state).stats;
export const selectProductCategories = (state: RootState) =>
  selectSlice(state).categories;
export const selectCurrentProduct = (state: RootState) =>
  selectSlice(state).current;
export const selectBulkSelection = (state: RootState) =>
  selectSlice(state).bulkSelection;

// Registers this slice in the typed root state without the platform importing it.
declare module '@/platform/store/rootReducer' {
  interface LazyLoadedSlices extends WithSlice<typeof productSlice> {}
}
